import * as React from 'react';
import { io } from 'socket.io-client';
import { createWithEqualityFn } from 'zustand/traditional';
import { shallow } from 'zustand/shallow';

// src/core/provider.tsx
var useSocketStore = createWithEqualityFn(
  (set, get) => ({
    socket: null,
    setSocket: (socket) => set({ socket }),
    options: {},
    setOptions: (options) => set({ options: { ...get().options, ...options } })
  }),
  shallow
);

// src/hooks/useConnect.ts
var useConnect = () => {
  const store = useSocketStore((state) => ({
    socket: state.socket,
    setSocket: state.setSocket
  }));
  const connect = (url, options) => {
    if (store.socket?.connected) {
      return;
    }
    const socket = io(url, options);
    store.setSocket(socket);
  };
  const disconnect = () => {
    if (!store.socket) {
      return;
    }
    store.socket.disconnect();
    store.setSocket(null);
  };
  return {
    connect,
    disconnect
  };
};

// src/core/provider.tsx
function SocketProvider({ uri, options, children }) {
  const { connect, disconnect } = useConnect();
  React.useEffect(() => {
    connect(uri, options);
    return () => {
      disconnect();
    };
  }, [uri, options]);
  return /* @__PURE__ */ React.createElement(React.Fragment, null, children);
}
function useListener(options, callback, deps = []) {
  const socket = useSocketStore((s) => s.socket);
  const eventName = typeof options === "string" ? options : options.eventName;
  const checkHasListeners = typeof options === "object" ? options.checkHasListeners : false;
  React.useEffect(() => {
    if (!socket)
      return;
    if (checkHasListeners && socket.hasListeners(eventName))
      return;
    const handler = (e) => callback(e);
    socket.on(eventName, handler);
    return () => {
      socket.off(eventName, handler);
    };
  }, [socket, eventName, checkHasListeners, callback, ...deps]);
  return () => socket?.off(eventName);
}

// src/hooks/useSocket.ts
function useSocket() {
  const socket = useSocketStore((s) => s.socket);
  return socket;
}

export { SocketProvider, useListener, useSocket };
